import { useEffect, useMemo } from 'react';

import { LabelsService } from '@/utils/services/LabelsService';

import { useAlbumManualFilters } from '@/utils/hooks/albums/useAlbumManualFilters';
import {
    ADD_LABEL,
    REMOVE_HASHTAG,
    REMOVE_LABEL,
    REMOVE_PERIOD_SELECTED,
    UPDATE_PERIOD_SELECTED,
    useAlbumContext
} from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import { TIKTOK_BUSINESS } from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import ActionTag from '@/components/base/Tag/ActionTag';
import DatepickerAlbums from '@/components/composite/Datepicker/DatepickerAlbums';
import HashtagsFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/HashtagsFilter';
import { SocialProfilesFilter } from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/SocialProfilesFilter';
import SelectAlbumTags from '@/components/pages/albums/AlbumFunnelModal/components/SelectAlbumTags';
import SelectPostType from '@/components/pages/albums/AlbumFunnelModal/components/SelectPostType';

const BORDER_CLASSNAME = 'rounded-md border border-icoGray-300 dark:border-icoDarkMode-beton';

const AlbumManualFilters = () => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();

    const {
        labelsOptions,
        selectedHashtags,
        selectedLabels,
        network,
        isStoryAlbum,
        isGroup,
        setRequestParams
    } = useAlbumManualFilters();

    const debouncedSearch = useMemo(
        () => LabelsService.createDebouncedSearch(setRequestParams),
        [setRequestParams]
    );

    useEffect(() => {
        return () => {
            debouncedSearch?.cancel?.();
        };
    }, [debouncedSearch]);

    const handleAddLabel = (item) => {
        dispatch({
            type: ADD_LABEL,
            payload: item?.label
        });
    };

    const handleRemoveLabel = (label) => {
        dispatch({
            type: REMOVE_LABEL,
            payload: label
        });
    };

    const handleRemoveHashtag = (hashtag) => {
        dispatch({
            type: REMOVE_HASHTAG,
            payload: hashtag
        });
    };

    const handleSetPeriod = (date) => {
        dispatch({ type: UPDATE_PERIOD_SELECTED, payload: date });
    };

    const handleRemovePeriod = () => {
        dispatch({ type: REMOVE_PERIOD_SELECTED });
    };

    const periodSelected =
        albumInfo?.criteria?.started_at && albumInfo?.criteria?.ended_at
            ? [albumInfo?.criteria?.started_at ?? '', albumInfo?.criteria?.ended_at ?? '']
            : '';

    const showPostTypeFilter = network !== TIKTOK_BUSINESS;

    // Filter out selected labels from options
    const availableLabelsOptions = labelsOptions?.filter(
        (label) => !albumInfo?.criteria?.labels?.includes(label?.label)
    );

    return (
        <div className="mb-5 space-y-3">
            <div className={classNames('grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4')}>
                {showPostTypeFilter && <SelectPostType className={BORDER_CLASSNAME} />}

                <SelectAlbumTags
                    className={BORDER_CLASSNAME}
                    options={availableLabelsOptions}
                    placeholder={
                        <span className="flex items-center space-x-3">
                            <Icon icon="search" />
                            <span>{t('modal.edit_modal.filter_on_label')}</span>
                        </span>
                    }
                    buttonStyle="noBorder"
                    idInput="labels-search"
                    addItem={handleAddLabel}
                    onSearch={debouncedSearch}
                    activeActionTags={false}
                />

                {!isStoryAlbum && (
                    <HashtagsFilter
                        placeholder={t('modal.edit_modal.filter_on_hashtag')}
                        showLabel={false}
                        forceManualInput
                        hideTagsList
                    />
                )}

                <DatepickerAlbums
                    className={BORDER_CLASSNAME}
                    inputStyle="datepicker"
                    noBorderShadow
                    isLeftIcon
                    periodSelected={periodSelected}
                    setPeriodSelected={handleSetPeriod}
                    removePeriod={handleRemovePeriod}
                    placeholder={t('modal.edit_modal.filter_on_period')}
                />

                {isGroup && <SocialProfilesFilter showLabel={false} />}
            </div>
            {(selectedHashtags?.length > 0 || selectedLabels?.length > 0) && (
                <ul className="flex flex-wrap gap-2">
                    {selectedHashtags.map((hashtag) => (
                        <li key={`hashtag-${hashtag}`}>
                            <ActionTag data-testid="input-action-tag">
                                <Icon icon="hashtag" className="p-1 align-bottom" />
                                <span className="mr-1">{hashtag.replace(/^#/, '')}</span>
                                <button
                                    aria-label="remove-hashtag"
                                    className="leading-none"
                                    type="button"
                                    onClick={() => handleRemoveHashtag(hashtag)}>
                                    <Icon icon="close" className="align-bottom" />
                                </button>
                            </ActionTag>
                        </li>
                    ))}
                    {selectedLabels.map((label) => (
                        <li key={`label-${label.label}`}>
                            <ActionTag data-testid="input-action-tag">
                                <Icon icon="labels" className="p-1 align-bottom" />
                                <span className="mr-1">{label.label}</span>
                                <button
                                    aria-label="remove-label"
                                    className="leading-none"
                                    type="button"
                                    onClick={() => handleRemoveLabel(label.label)}>
                                    <Icon icon="close" className="align-bottom" />
                                </button>
                            </ActionTag>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AlbumManualFilters;
